// vacChat.ts
import type { VacChatParams } from '@/types';
import { langfuseProxy } from '@/utils/langfuseProxy';
import { 
  createBackendCallSpan, 
  markFirstBackendResponse,
  updateStreamingProgress,
  completeStreamingGeneration,
  completeUserInteractionTrace,
  createErrorSpan
} from '@/utils/langfuseEnhanced';

const STREAM_TIMEOUT = 2400000; // 240 seconds (4 minutes) to accommodate large file processing

console.log('VacChat initialized with server-side Langfuse proxy');

// Per-session stream locks to prevent concurrent requests from same user/session
const streamLocks = new Map<string, { 
    isStreaming: boolean; 
    timestamp: number; 
    requestId?: string;
    userId?: string;
}>();

// Cleanup interval to remove stale locks
const CLEANUP_INTERVAL = 30000; // 30 seconds
setInterval(() => {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    streamLocks.forEach((lock, lockKey) => {
        if (lock.isStreaming && (now - lock.timestamp) > STREAM_TIMEOUT) {
            console.warn('Removing stale stream lock', { 
                lockKey,
                requestId: lock.requestId,
                userId: lock.userId,
                age: now - lock.timestamp
            });
            keysToDelete.push(lockKey);
        }
    });
    
    keysToDelete.forEach(key => streamLocks.delete(key));
}, CLEANUP_INTERVAL);

// Create a lock key based on user/session context
const createLockKey = (params: VacChatParams): string => {
    // Priority order: sessionId > userId > trace_id prefix
    const sessionId = params.sessionId || params.emissaryConfig?.currentUser?.sessionId;
    const userId = params.userId || params.emissaryConfig?.currentUser?.uid || params.emissaryConfig?.currentUser?.email;
    const traceId = params.traceId as string | undefined;
    
    if (sessionId) {
        return `session:${sessionId}`;
    } else if (userId) {
        return `user:${userId}`;
    } else if (traceId && typeof traceId === 'string') {
        // Extract session-like identifier from trace_id if possible
        const tracePrefix = traceId.split('-')[0];
        return `trace:${tracePrefix}`;
    } else {
        // Fallback to a global lock (shouldn't happen in normal usage)
        console.warn('No session/user identifier found, using global lock');
        return 'global';
    }
};

// Check and acquire lock for a specific session/user
const acquireLock = (lockKey: string, requestId: string, userId?: string): boolean => {
    const existingLock = streamLocks.get(lockKey);
    const now = Date.now();
    
    // Check if existing lock is stale
    if (existingLock && existingLock.isStreaming && (now - existingLock.timestamp) > STREAM_TIMEOUT) {
        console.warn('Detected stale stream lock, removing', { 
            lockKey,
            requestId: existingLock.requestId,
            age: now - existingLock.timestamp
        });
        streamLocks.delete(lockKey);
    }
    
    // Check if lock is still active
    const currentLock = streamLocks.get(lockKey);
    if (currentLock && currentLock.isStreaming) {
        return false; // Lock is held
    }
    
    // Acquire lock
    streamLocks.set(lockKey, {
        isStreaming: true,
        timestamp: now,
        requestId,
        userId
    });
    
    return true; // Lock acquired
};

// Release lock for a specific session/user
const releaseLock = (lockKey: string) => {
    streamLocks.delete(lockKey);
};

export async function vacChat(params: VacChatParams) {
    // Create unique request ID and timer labels
    const requestId = Math.random().toString(36).substring(7);
    
    // Get the existing Langfuse trace or create a new one
    const traceId = params.traceId || `ui2-${Date.now()}-${requestId}`;
    let trace: any = { id: traceId }; // Simplified trace object for proxy usage
    let backendCallSpan: any;
    let streamingGeneration: any;
    
    // Only create a new trace if we don't have an existing traceId from upstream
    // If traceId is provided by params, it means a trace was already created
    const shouldCreateNewTrace = !params.traceId && !params.langfuseTrace;
    
    if (shouldCreateNewTrace) {
        try {
            await langfuseProxy.createTrace({
                traceId: traceId,
                name: (params.emissaryConfig?.name as string) ?? 'Assistant',  // Use assistant name
                sessionId: params.sessionId || undefined,
                userId: params.emissaryConfig?.currentUser?.email || undefined,
                metadata: {
                    requestId,
                    emissaryConfig: params.emissaryConfig,
                }
            });
            console.log(`Created new langfuse trace via proxy ${traceId}`);
        } catch (error) {
            console.warn('Failed to create Langfuse trace via proxy:', error);
        }
    } else {
        console.log(`Using existing trace ${traceId} - not creating new one`);
        if (params.langfuseTrace) {
            trace = params.langfuseTrace;
        }
    }
    
    // Create backend call span
    backendCallSpan = await createBackendCallSpan(trace, {
        requestId,
        message: params.userMessage,
        historyLength: params.chatHistory?.length || 0
    });
    
    // Track start time and other variables for the finally block
    const startTime = Date.now();
    let chunkCount = 0;
    const timerLabels = {
        total: `vacChat_total_${requestId}`,
        lockCheck: `vacChat_lock_check_${requestId}`,
        prepareRequest: `vacChat_prepare_request_${requestId}`,
        fetch: `vacChat_fetch_${requestId}`,
        processStream: `vacChat_process_stream_${requestId}`
    };
    
    console.time(timerLabels.total);
    console.time(timerLabels.lockCheck);
    
    // Create lock key based on session/user context
    const lockKey = createLockKey(params);
    console.log('Using lock key:', { lockKey, requestId });
    
    // Try to acquire lock for this session/user
    const userId = (params.userId || params.emissaryConfig?.currentUser?.uid || params.emissaryConfig?.currentUser?.email) as string | undefined;
    const lockAcquired = acquireLock(lockKey, requestId, userId);
    
    if (!lockAcquired) {
        const existingLock = streamLocks.get(lockKey);
        console.warn('Stream already in progress for this session/user', { 
            lockKey,
            requestId,
            existingRequestId: existingLock?.requestId,
            userId
        });
        throw new Error('A streaming request is already in progress for this session');
    }
    
    console.timeEnd(timerLabels.lockCheck);

    let controller: AbortController | null = null;
    let accumulatedContent = '';
    let abortListener: ((event: Event) => void) | null = null;
    let userAborted = false; // Flag to track if abort was user-initiated
    let chatError: Error | undefined;

    try {
        console.time(timerLabels.prepareRequest);
        
        // Lock is already acquired in lockCheck phase above
        
        // Skip creating prepare span - already have frontend-ui-processing span
        
        // Create a new AbortController that can be triggered either by timeout or user action
        controller = new AbortController();
        
        // If an external signal is provided, listen to it
        if (params.signal) {
            // If the external signal is already aborted, abort immediately
            if (params.signal.aborted) {
                userAborted = true;
                controller.abort();
                throw new DOMException('Request aborted by user', 'AbortError');
            }
            
            // Otherwise, listen for future abort signals
            abortListener = () => {
                userAborted = true;
                controller?.abort();
                console.log('Request aborted by user', { requestId });
            };
            
            params.signal.addEventListener('abort', abortListener);
        }
        
        // Extract all params except onBotMessage to avoid context issues
        const { 
            onBotMessage, 
            userMessage,
            chatHistory,
            humanChatHistory,
            apiEndpoint,
            signal,
            skipInitialMessages = false, // New option with default
            ...configParams // Everything else goes here
        } = params;

        // Set stream_wait_time - backend expects integer (milliseconds or seconds)
        // Using 0 for minimal wait time
        const streamWaitTime = 0; // Using integer 0 for minimal wait

        const requestBody = {
            endpoint: apiEndpoint,
            user_input: userMessage,
            chat_history: chatHistory,
            humanChatHistory: humanChatHistory,
            isStreaming: true,
            stream_only: true,
            stream_wait_time: streamWaitTime,
            trace_id: traceId,
            selectedItems: params.emissaryConfig?.selectedItems,
            ...configParams
        };
        
        console.timeEnd(timerLabels.prepareRequest);
        
        // Skip ending prepare span - using frontend-ui-processing span instead

        // Only send the connecting messages if not explicitly skipped
        if (!skipInitialMessages) {
            // Send an immediate notification to the UI that we're connecting
            params.onBotMessage({
                sender: 'bot',
                content: '<thinking>Connecting.</thinking>',
            });
        }
        
        console.time(timerLabels.fetch);
        
        // Log when we're about to make the API call
        console.log('Initiating API call', { 
            requestId,
            timestamp: new Date().toISOString()
        });
        
        // Diagnostic logging for trace_id
        console.log('🔍 [TRACE DEBUG] Frontend sending trace_id:', {
            traceId: traceId,
            inHeaders: true,
            headerName: 'X-Trace-ID',
            requestId: requestId,
            timestamp: new Date().toISOString()
        });
        
        // Set up a better timeout system - 2 minutes before warning
        const initialResponseTimeout = 240000; // 4 minutes for large file processing
        const timeoutId = setTimeout(() => {
            // Don't abort automatically - instead notify user
            // Only abort if there's been ZERO content received
            if (!accumulatedContent) {
                params.onBotMessage({
                    sender: 'bot',
                    content: '<thinking>Response is taking longer than expected. You can continue waiting or stop the request.</thinking>',
                });
                
                console.warn('Client timeout reached for initial response with no content', { requestId });
            } else {
                // If we've received SOME content, just log a warning but don't abort
                console.warn('Slow response detected but content is being received', { 
                    requestId,
                    contentLength: accumulatedContent.length 
                });
            }
        }, initialResponseTimeout);

        const response = await fetch('/api/proxy', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
                'Connection': 'keep-alive',
                'X-Request-ID': requestId,
                'X-Trace-ID': traceId,  // Pass trace ID to backend for proper trace hierarchy
                // Add Langfuse tracing headers
                'X-User-ID': params.userId || params.emissaryConfig?.currentUser?.uid || '',
                'X-Session-ID': params.emissaryConfig?.currentUser?.sessionId || '',
                'X-Message-Source': 'web-app'
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal
        });
        
        // Important: Clear timeout as soon as we get ANY response
        clearTimeout(timeoutId);
        
        console.timeEnd(timerLabels.fetch);
        console.log('API response received', { 
            requestId,
            timestamp: new Date().toISOString(),
            status: response.status
        });
        
        // Mark first backend response and create streaming generation
        const responseTime = Date.now() - startTime;
        streamingGeneration = await markFirstBackendResponse(backendCallSpan, trace, responseTime);
        
        // Track latency if latencyTracker was provided
        if (params.latencyTracker && typeof params.latencyTracker === 'object' && 'mark' in params.latencyTracker) {
            (params.latencyTracker as any).mark('first_backend_response');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            await createErrorSpan(trace, new Error(`HTTP error! status: ${response.status}`), 'api_response');
            throw new Error(`HTTP error! status: ${response.status}`, { 
                cause: errorData 
            });
        }

        // Only send the connected message if not explicitly skipped
        if (!skipInitialMessages) {
            // Signal successful connection to backend
            params.onBotMessage({
                sender: 'bot',
                content: '<thinking>Connected.</thinking>',
            });
        }

        console.time(timerLabels.processStream);
        
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader available');

        const decoder = new TextDecoder();
        let done = false;
        let lastSentContent = '';
        
        try {
            while (!done) {
                const result = await reader.read();
                done = result.done;
                
                if (done) {
                    if (accumulatedContent && accumulatedContent !== lastSentContent) {
                        const newContent = accumulatedContent.slice(lastSentContent.length);
                        if (newContent.length > 0) {
                            lastSentContent = accumulatedContent;
                            params.onBotMessage({ 
                                sender: 'bot', 
                                content: newContent,
                            });
                        }
                    }
                    break;
                }
                
                const chunk = decoder.decode(result.value, { stream: true });
        
                if (chunk) {
                    // Parse SSE format: data: {...}\n\n
                    const lines = chunk.split('\n');
                    
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.slice(6); // Remove 'data: ' prefix
                            
                            if (data === '[DONE]') {
                                // Stream is complete
                                done = true;
                                break;
                            }
                            
                            try {
                                // Try to parse as JSON (for {"chunk": "..."} format)
                                const parsed = JSON.parse(data);
                                if (parsed.chunk) {
                                    // This is a streaming chunk
                                    accumulatedContent += parsed.chunk;
                                    chunkCount++;
                                    const newContent = accumulatedContent.slice(lastSentContent.length);
                                    
                                    // Update lastSentContent immediately
                                    lastSentContent = accumulatedContent;
                                    
                                    // Send to UI immediately without additional length checks
                                    params.onBotMessage({ 
                                        sender: 'bot', 
                                        content: newContent,
                                    });
                                } else if (parsed.answer) {
                                    // This is the final answer with metadata
                                    // The accumulated content should already have the full response
                                    // Just ensure we send any remaining content
                                    if (accumulatedContent !== lastSentContent) {
                                        const finalContent = accumulatedContent.slice(lastSentContent.length);
                                        if (finalContent.length > 0) {
                                            params.onBotMessage({ 
                                                sender: 'bot', 
                                                content: finalContent,
                                            });
                                        }
                                    }
                                }
                            } catch (e) {
                                // If not JSON, treat as plain text (shouldn't happen with SSE endpoint)
                                console.warn('Failed to parse SSE data as JSON:', data, e);
                            }
                        }
                    }
                    
                    // Update streaming progress in Langfuse
                    if (streamingGeneration && chunkCount % 10 === 0) { // Update every 10 chunks
                        updateStreamingProgress(streamingGeneration, chunkCount, accumulatedContent.length);
                    }
                    
                    // Track latency if latencyTracker was provided
                    if (params.latencyTracker && typeof params.latencyTracker === 'object' && 'mark' in params.latencyTracker && chunkCount === 1) {
                        (params.latencyTracker as any).mark('first_chunk_received');
                    }
                }
            }
            
            // Final flush of the decoder
            const final = decoder.decode(undefined);
            if (final) {
                accumulatedContent += final;
                const newContent = accumulatedContent.slice(lastSentContent.length);
                
                if (newContent.length > 0) {
                    lastSentContent = accumulatedContent;
                    params.onBotMessage({ 
                        sender: 'bot', 
                        content: newContent,
                    });
                }
            }
        
            console.log('Stream completed, total content length:', accumulatedContent.length);
            
            // Complete streaming generation
            if (streamingGeneration) {
                const totalDuration = Date.now() - startTime;
                completeStreamingGeneration(streamingGeneration, accumulatedContent, totalDuration, {
                    chunkCount,
                    requestId
                });
            }
            
            // Track completion latency
            if (params.latencyTracker && typeof params.latencyTracker === 'object' && 'mark' in params.latencyTracker) {
                (params.latencyTracker as any).mark('streaming_complete');
            }
            
        } finally {
            console.timeEnd(timerLabels.processStream);
            try {
                await reader.cancel();
            } catch (cancelError) {
                console.warn('Error canceling reader:', cancelError, { 
                    requestId 
                });
            }
        }
    } catch (error: unknown) {
        console.error('Streaming error:', error, { requestId });
        
        // Store the error for the finally block
        chatError = error instanceof Error ? error : new Error(String(error));
        
        // Create error span in Langfuse
        await createErrorSpan(trace, chatError, 'streaming');
        
        // Track error latency
        if (params.latencyTracker && typeof params.latencyTracker === 'object' && 'mark' in params.latencyTracker) {
            (params.latencyTracker as any).mark('error_occurred');
        }
        // Handle abort errors differently - don't show an error message to the user
        if (error instanceof DOMException && error.name === 'AbortError') {
            // Add information about whether this was a user abort
            console.log('Request was aborted', { 
                requestId,
                userInitiated: userAborted
            });
            
            // Re-throw as AbortError to let caller know this was a user-initiated abort
            throw new DOMException(
                userAborted ? 'The user aborted a request.' : 'The request timed out.',
                'AbortError'
            );
        }
        
        // For all other errors, show an error message if we haven't received any content yet
        if (!accumulatedContent) {
            params.onBotMessage({ 
                sender: 'bot', 
                content: 'Sorry, an error occurred. Please try again.',
            });
        }
        throw error;
    } finally {
        console.timeEnd(timerLabels.total);
        
        const totalDuration = Date.now() - startTime;
        const success = !chatError;
        
        // Complete the user interaction trace
        if (backendCallSpan) {
            await completeUserInteractionTrace(
                trace, 
                backendCallSpan, 
                success, 
                totalDuration,
                {
                    contentLength: accumulatedContent.length,
                    chunkCount,
                    requestId,
                    latencies: params.latencyTracker && typeof params.latencyTracker === 'object' && 'getAllLatencies' in params.latencyTracker 
                        ? (params.latencyTracker as any).getAllLatencies() 
                        : undefined
                }
            );
        }
        
        // Flush Langfuse events via proxy
        try {
            await langfuseProxy.flush();
            console.log(`Flushed end langfuse via proxy`)
        } catch (flushError) {
            console.error('Error flushing Langfuse events via proxy:', flushError);
        }
        
        if (params.signal && abortListener) {
            params.signal.removeEventListener('abort', abortListener);
        }
        controller?.abort();
        
        // Release the session/user-specific lock
        releaseLock(lockKey);
        console.log('Released stream lock', { lockKey, requestId });
    }
}